// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BundleSortProperty, BundlesSearchParams } from '@/bundle/api/types';
import { BundlesTable } from '@/bundle/components/bundles-table';
import { BundlesTableSkeleton } from '@/bundle/components/bundles-table-skeleton';
import { CreateBundleDialog } from '@/bundle/components/create-bundle-dialog';
import { CatalogPageHeader } from '@/components/catalog-page-header';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ClearRounded,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort, invert } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { debounce, omit } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';

// Debounce interval for the search input, in milliseconds
const SEARCH_DEBOUNCE_MS = 300;

// Help text for archive filter tooltip
const SHOW_ARCHIVED_TOOLTIP_TITLE =
  'Show all bundle records, including archived bundles. Archived bundles do not appear by default in the bundle list.';

interface Props {
  searchParams: BundlesSearchParams;
  initialBundleName?: string;
}

/**
 * The bundles list page: a searchable, sortable, paginated table of bundles
 * with an archived filter and a create dialog.
 */
export function BundlesListing(props: Props) {
  const { searchParams, initialBundleName } = props;
  const navigate = useNavigate();
  const searchContainer = useRef<HTMLInputElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const settingsMenu = useDisclosure();
  // If the 'new' search parameter is present, automatically open the creation dialog
  const createBundleDialog = useDisclosure(searchParams.new !== undefined);

  const sort: Sort<BundleSortProperty> = {
    property: searchParams.sort ?? 'name',
    direction: searchParams.direction ?? 'asc',
  };

  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      if (query.length === 0) {
        navigate({
          to: '/catalog/bundles',
          search: omit(searchParams, 'page', 'query'),
        });
        return;
      }
      navigate({
        to: '/catalog/bundles',
        search: { ...omit(searchParams, 'page'), query },
      });
    }, SEARCH_DEBOUNCE_MS);
  }, [navigate, searchParams]);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  const handleClearSearch = () => {
    if (searchContainer.current) {
      searchContainer.current.value = '';
    }
    updateQuery('');
  };

  // Reset pagination alongside the filter change, like the search handler,
  // so a page valid under one filter cannot overrun the other
  const handleChangeArchivedFilter = (isArchived: boolean) => {
    if (isArchived) {
      navigate({
        to: '/catalog/bundles',
        search: { ...omit(searchParams, 'page'), archived: true },
      });
    } else {
      navigate({
        to: '/catalog/bundles',
        search: omit(searchParams, 'page', 'archived'),
      });
    }
  };

  const handleSort = (property: BundleSortProperty) => {
    if (property === sort.property) {
      navigate({
        to: '/catalog/bundles',
        search: { ...searchParams, direction: invert(sort.direction) },
      });
    } else {
      navigate({
        to: '/catalog/bundles',
        search: { ...searchParams, sort: property, direction: 'asc' },
      });
    }
  };

  const handlePageChange = (page: number) => {
    navigate({
      to: '/catalog/bundles',
      search: { ...searchParams, page },
    });
  };

  // Closes the create bundle dialog and removes the 'new' parameter from the URL
  const handleCloseCreateDialog = () => {
    createBundleDialog.close();
    navigate({
      to: '/catalog/bundles',
      search: omit(searchParams, 'new'),
    });
  };

  const isArchivedChecked = searchParams.archived ?? false;
  const isBadgeInvisible = searchParams.archived === undefined;

  return (
    <PageContent>
      {/* Header */}
      <CatalogPageHeader
        action={
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={createBundleDialog.open}
          >
            Add bundle
          </Button>
        }
      />

      {/* Table controls */}
      <TableControlContainer>
        <Box marginRight="auto" />
        <TextField
          inputRef={searchContainer}
          label="Search bundles"
          size="small"
          defaultValue={searchParams.query ?? ''}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleSearch}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
          InputProps={{
            endAdornment: searchParams.query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                  aria-label="Clear search"
                >
                  <ClearRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <IconButton ref={settingsButtonRef} onClick={settingsMenu.open}>
          <Badge variant="dot" color="error" invisible={isBadgeInvisible}>
            <SettingsRounded fontSize="small" />
          </Badge>
        </IconButton>
        <Popover
          anchorEl={settingsButtonRef.current}
          open={settingsMenu.isOpen}
          onClose={settingsMenu.close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Stack paddingY={0.5}>
            <Tooltip arrow placement="left" title={SHOW_ARCHIVED_TOOLTIP_TITLE}>
              <FormControlLabel
                sx={{ paddingLeft: 2, paddingY: 0.5 }}
                label={
                  <Typography variant="body2">
                    Include archived bundles
                  </Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={isArchivedChecked}
                    onChange={(_, isChecked) =>
                      handleChangeArchivedFilter(isChecked)
                    }
                  />
                }
              />
            </Tooltip>
          </Stack>
        </Popover>
      </TableControlContainer>

      {/* Bundles table */}
      <SupportErrorBoundary message="An error occurred while loading bundles.">
        <Suspense fallback={<BundlesTableSkeleton />}>
          <BundlesTable
            searchQuery={searchParams.query}
            includeArchived={searchParams.archived}
            sort={sort}
            onSort={handleSort}
            page={searchParams.page}
            onPageChange={handlePageChange}
            onAddBundle={createBundleDialog.open}
          />
        </Suspense>
      </SupportErrorBoundary>

      {/* Create bundle dialog */}
      <CreateBundleDialog
        isOpen={createBundleDialog.isOpen}
        onClose={handleCloseCreateDialog}
        initialName={initialBundleName}
      />
    </PageContent>
  );
}
